import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useParams } from "react-router-dom";
import { Page, PageTopbarItem } from "../../../commons/layout/Page.js";
import { PageContainer } from "../../../commons/layout/PageContainer.js";
import { PageGrid } from "../../../commons/layout/PageGrid.js";
import { TemplateTypeOverviewPropertyBox } from "../../../features/templateTypes/TemplateTypeOverviewPropertyBox.js";
import { useTemplateTypeOverviewQuery } from "./TemplateTypeOverview.generated.js";
import { TemplateTypeOverviewSubHeader } from "./TemplateTypeOverviewSubHeader.js";
import { TemplateTypeOverviewHeaderBox } from "../../../features/templateTypes/TemplateTypeOverviewHeaderBox.js";

interface Props {
  submenuItems: PageTopbarItem[];
  pathToDocList: string;
}

export const TemplateTypeOverview = ({
  submenuItems,
  pathToDocList,
}: Props) => {
  const { id } = useParams();
  if (!id) throw new Error("TemplateType id is missing");

  const { t } = useTranslate(["TemplateTypes"]);

  const client = useApolloClient();
  const query = useTemplateTypeOverviewQuery({
    client,
    variables: {
      id: id,
    },
  });

  const templateType = getDataOrNull(query.data?.templateType);

  const breadcrumbs = React.useMemo(
    () =>
      templateType
        ? [
            {
              title: t("Template types", { ns: "TemplateTypes" }),
              to: pathToDocList,
            },
            {
              title: templateType.title,
              to: `/${pathToDocList}/${templateType.id}`,
            },
          ]
        : undefined,
    [pathToDocList, t, templateType]
  );

  return (
    <Page
      title={templateType?.title ?? ""}
      subtitle={t("Template types", { ns: "TemplateTypes" })}
      submenuItems={submenuItems}
      breadcrumbs={breadcrumbs}
      subHeader={
        templateType ? (
          <TemplateTypeOverviewSubHeader
            templateType={templateType}
            isHeaderVisible={undefined as never}
            setHeaderVisible={undefined as never}
            pathToDocList={pathToDocList}
          />
        ) : undefined
      }
    >
      {templateType && (
        <PageContainer>
          <PageGrid columns={{ xs: 1, md: 2, xl: 4 }}>
            <>
              <TemplateTypeOverviewHeaderBox templateType={templateType} />
            </>
            <>
              <TemplateTypeOverviewPropertyBox templateType={templateType} />
            </>
          </PageGrid>
        </PageContainer>
      )}
    </Page>
  );
};
